import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import axios from 'axios';
import { 
    Lock, LogOut, Sun, Moon, Users, Eye, Calendar, TrendingUp,
    PenLine, Beaker, Star, Briefcase, Mail, Settings, FileText, BarChart3
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Toaster } from '../components/ui/sonner';
import { toast } from 'sonner';

import {
    StatCard,
    WritingsManager,
    ExperimentsManager,
    RecommendationsManager,
    CareerManager,
    ResearchManager,
    SubscribersManager,
    SettingsManager,
    AnalyticsManager
} from '../components/admin';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Login Component
function LoginForm() {
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const { login } = useAuth();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await login(password);
            toast.success('Login successful!');
        } catch (error) {
            toast.error('Invalid password');
        }
        setLoading(false);
    };

    return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="login-page">
            <div className="retro-card p-8 w-full max-w-md">
                <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-primary mx-auto flex items-center justify-center mb-4">
                        <Lock className="h-8 w-8 text-primary-foreground" />
                    </div>
                    <h1 
                        className="text-2xl uppercase tracking-tight"
                        style={{ fontFamily: "'Silkscreen', cursive" }}
                    >
                        Admin Access
                    </h1>
                    <p className="text-sm text-muted-foreground mt-2">
                        Enter password to continue
                    </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <Input
                        type="password"
                        placeholder="Password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="rounded-none border-2"
                        data-testid="login-password-input"
                    />
                    <Button 
                        type="submit" 
                        className="w-full rounded-none border-2 border-primary bg-primary text-primary-foreground retro-btn"
                        disabled={loading}
                        data-testid="login-submit-btn"
                    >
                        {loading ? 'Logging in...' : 'Enter Dashboard'}
                    </Button>
                </form>
            </div>
        </div>
    );
}

// Dashboard Component
function Dashboard() {
    const { logout, token } = useAuth();
    const { theme, toggleTheme } = useTheme();
    const [stats, setStats] = useState({ total_visits: 0, unique_visitors: 0, today_visits: 0, this_week_visits: 0 });
    const [writings, setWritings] = useState([]);
    const [experiments, setExperiments] = useState([]);
    const [recommendations, setRecommendations] = useState([]);
    const [research, setResearch] = useState([]);
    const [researchRequests, setResearchRequests] = useState([]);
    const [career, setCareer] = useState([]);
    const [subscribers, setSubscribers] = useState([]);
    const [siteSettings, setSiteSettings] = useState({
        writings_empty_message: "I fed all of my writing to my AI Agent. It's now out in the wild trying to take over the world. BRB.",
        recommendations_empty_message: "Patience. I'm watching YouTube for your next recco :P",
        experiments_empty_message: "An evil scientist stole the experiments. Please stay tuned.",
        research_empty_message: "Research in progress. The lab is under construction.",
        flip_card_hint: "flip to reveal your new fav character"
    });
    const [loading, setLoading] = useState(true);

    const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

    const fetchAllData = useCallback(async () => {
        try {
            const [statsRes, writingsRes, experimentsRes, recommendationsRes, researchRes, researchReqRes, careerRes, subscribersRes, settingsRes] = await Promise.all([
                axios.get(`${API}/stats`, { headers }),
                axios.get(`${API}/writings`, { headers }),
                axios.get(`${API}/experiments`, { headers }),
                axios.get(`${API}/recommendations`, { headers }),
                axios.get(`${API}/research`, { headers }),
                axios.get(`${API}/research-requests`, { headers }),
                axios.get(`${API}/career`, { headers }),
                axios.get(`${API}/subscribers`, { headers }),
                axios.get(`${API}/settings`)
            ]);
            setStats(statsRes.data);
            setWritings(writingsRes.data);
            setExperiments(experimentsRes.data);
            setRecommendations(recommendationsRes.data);
            setResearch(researchRes.data);
            setResearchRequests(researchReqRes.data);
            setCareer(careerRes.data);
            setSubscribers(subscribersRes.data);
            setSiteSettings(settingsRes.data);
        } catch (error) {
            toast.error('Failed to fetch data');
        }
        setLoading(false);
    }, [headers]);

    useEffect(() => {
        fetchAllData();
    }, [fetchAllData]);

    return (
        <div className="min-h-screen bg-background" data-testid="admin-dashboard">
            {/* Header */}
            <header className="sticky top-0 z-50 bg-background border-b-2 border-border">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        <h1 
                            className="text-xl uppercase tracking-tight"
                            style={{ fontFamily: "'Silkscreen', cursive" }}
                        >
                            Dashboard
                        </h1>
                        <div className="flex items-center gap-3">
                            <Button
                                variant="outline"
                                size="icon"
                                onClick={toggleTheme}
                                className="rounded-none border-2"
                                data-testid="admin-theme-toggle"
                            >
                                {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                            </Button>
                            <Button
                                variant="outline"
                                onClick={logout}
                                className="rounded-none border-2 text-xs uppercase tracking-widest"
                                data-testid="logout-btn"
                            >
                                <LogOut className="h-4 w-4 mr-2" />
                                Logout
                            </Button>
                        </div>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Stats Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                    <StatCard icon={<Eye />} label="Total Visits" value={stats.total_visits} />
                    <StatCard icon={<Users />} label="Unique Visitors" value={stats.unique_visitors} />
                    <StatCard icon={<Calendar />} label="Today" value={stats.today_visits} />
                    <StatCard icon={<TrendingUp />} label="This Week" value={stats.this_week_visits} />
                </div>

                {/* Content Management Tabs */}
                <Tabs defaultValue="writings" className="space-y-4">
                    <TabsList className="rounded-none border-2 border-border bg-transparent p-1 gap-1 flex-wrap h-auto">
                        <TabsTrigger value="writings" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs uppercase tracking-widest">
                            <PenLine className="h-4 w-4 mr-2" />
                            Writings
                        </TabsTrigger>
                        <TabsTrigger value="experiments" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs uppercase tracking-widest">
                            <Beaker className="h-4 w-4 mr-2" />
                            Experiments
                        </TabsTrigger>
                        <TabsTrigger value="recommendations" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs uppercase tracking-widest">
                            <Star className="h-4 w-4 mr-2" />
                            Recommendations
                        </TabsTrigger>
                        <TabsTrigger value="career" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs uppercase tracking-widest">
                            <Briefcase className="h-4 w-4 mr-2" />
                            Career
                        </TabsTrigger>
                        <TabsTrigger value="research" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs uppercase tracking-widest">
                            <FileText className="h-4 w-4 mr-2" />
                            Research
                        </TabsTrigger>
                        <TabsTrigger value="subscribers" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs uppercase tracking-widest">
                            <Mail className="h-4 w-4 mr-2" />
                            Subscribers
                        </TabsTrigger>
                        <TabsTrigger value="analytics" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs uppercase tracking-widest">
                            <BarChart3 className="h-4 w-4 mr-2" />
                            Analytics
                        </TabsTrigger>
                        <TabsTrigger value="settings" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs uppercase tracking-widest">
                            <Settings className="h-4 w-4 mr-2" />
                            Settings
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="writings">
                        <WritingsManager writings={writings} onRefresh={fetchAllData} headers={headers} />
                    </TabsContent>
                    <TabsContent value="experiments">
                        <ExperimentsManager experiments={experiments} onRefresh={fetchAllData} headers={headers} />
                    </TabsContent>
                    <TabsContent value="recommendations">
                        <RecommendationsManager 
                            recommendations={recommendations} 
                            onRefresh={fetchAllData} 
                            headers={headers} 
                            categories={siteSettings.recommendation_categories || ['book', 'tool', 'resource']}
                            onCategoriesChange={fetchAllData}
                        />
                    </TabsContent>
                    <TabsContent value="career">
                        <CareerManager career={career} onRefresh={fetchAllData} headers={headers} />
                    </TabsContent>
                    <TabsContent value="research">
                        <ResearchManager research={research} researchRequests={researchRequests} onRefresh={fetchAllData} headers={headers} settings={siteSettings} />
                    </TabsContent>
                    <TabsContent value="subscribers">
                        <SubscribersManager subscribers={subscribers} onRefresh={fetchAllData} headers={headers} />
                    </TabsContent>
                    <TabsContent value="analytics">
                        <AnalyticsManager headers={headers} />
                    </TabsContent>
                    <TabsContent value="settings">
                        <SettingsManager settings={siteSettings} onRefresh={fetchAllData} headers={headers} />
                    </TabsContent>
                </Tabs>
            </main>
        </div>
    );
}

// Main Admin Page
export default function Admin() {
    const { isAuthenticated, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="text-center">
                    <div className="w-12 h-12 border-4 border-primary border-t-transparent animate-spin mx-auto mb-4" />
                    <p className="text-sm text-muted-foreground uppercase tracking-widest">Loading...</p>
                </div>
            </div>
        );
    }

    return (
        <>
            <Toaster position="top-right" />
            {isAuthenticated ? <Dashboard /> : <LoginForm />}
        </>
    );
}
